import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Logo } from './Logo';
import { useCheckLoginId, useSignUp, checkEmail } from '../services/userService';
import { emailVerificationService } from '../services/emailVerificationService';
import type { PendingSignupData } from './SignupFlow';
import { useNotifications } from '../hooks/useNotifications';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { linkLoginIdToEmail } from '../services/passwordResetService';
import { useAuth } from '../contexts/AuthContext';
import GoogleLoginButton from './GoogleLoginButton';
import MicrosoftLoginButton from './MicrosoftLoginButton';
import googleAnalytics from '../services/googleAnalyticsService';

interface SignupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToLogin: () => void;
  onSignupSuccess?: (userData: { loginId: string; name: string; email?: string }) => void;
  code?: string;
  verifiedEmail?: string;
  /** When set, first submit sends verification code to email and calls this with form data (no signup API yet). */
  onSendVerificationCode?: (formData: PendingSignupData) => void;
}

interface FormErrors {
  loginId?: string;
  email?: string;
  loginPwd?: string;
  loginPwd_confirm?: string;
  code?: string;
  ageConfirmation?: string;
  general?: string;
}

const SignupModal: React.FC<SignupModalProps> = ({
  isOpen,
  onClose,
  onSwitchToLogin,
  onSignupSuccess,
  code = '',
  verifiedEmail,
  onSendVerificationCode,
}) => {
  const { t } = useTranslation();
  const { success, info } = useNotifications();
  const { handleGoogleLogin, handleMicrosoftLogin, isGoogleLoggingIn, isMicrosoftLoggingIn, errors: authErrors, setErrors: setAuthErrors } = useAuth();
  
  // Lock body scroll when modal is open
  useBodyScrollLock(isOpen);

  // Helper function to translate backend error messages
  const translateBackendError = (errorMessage: string): string => {
    const errorLower = errorMessage.toLowerCase();
    
    // Map common backend error messages to translation keys
    // Login ID errors
    if (errorLower.includes('login id already exists') || 
        errorLower.includes('id is already taken') || 
        errorLower.includes('id already in use') ||
        errorLower.includes('this login id is already taken')) {
      return t('auth.signup.validation.idTaken');
    }
    if (errorLower.includes('login id is required') || errorLower.includes('id is required')) {
      return t('auth.signup.validation.idRequired');
    }
    
    // Email errors
    if (errorLower.includes('email already exists') || 
        errorLower.includes('email is already registered') ||
        errorLower.includes('this email is already registered')) {
      return t('auth.signup.validation.emailAlreadyRegistered');
    }
    if (errorLower.includes('email is required')) {
      return t('auth.signup.validation.emailRequired');
    }
    if (errorLower.includes('invalid email') || errorLower.includes('email invalid')) {
      return t('auth.signup.validation.emailInvalid');
      }
    
    // Password errors
    if (errorLower.includes('password') || errorLower.includes('pwd')) {
      if (errorLower.includes('match') || errorLower.includes('confirm') || errorLower.includes('do not match')) {
        return t('auth.signup.validation.passwordsNotMatch');
      }
      if (errorLower.includes('required')) {
      return t('auth.signup.validation.passwordRequired');
    }
      if (errorLower.includes('at least') || errorLower.includes('minimum') || errorLower.includes('6 characters')) {
        return t('auth.signup.validation.passwordMinLength');
    }
      return t('auth.signup.validation.passwordRequired');
    }
    
    // Referral code errors
    if (errorLower.includes('referral') || errorLower.includes('code')) {
      return t('auth.signup.validation.idCheckFailed');
    }
    
    // Validation failed
    if (errorLower.includes('validation failed')) {
      return t('auth.signup.validation.signupFailed');
    }
    
    // If no mapping found, return original message (backend might have its own translations)
    return errorMessage;
  };
  
  const signUpMutation = useSignUp();
  const [formData, setFormData] = useState({
    loginId: '',
    email: '',
    loginPwd: '',
    loginPwd_confirm: '',
    code: code,
  });
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const checkLoginIdMutation = useCheckLoginId();
  const [errors, setErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);

  // Track if code came from URL parameter
  const isCodeFromUrl = Boolean(code);
  
  // Update formData when code prop changes
  useEffect(() => {
    if (code) {
      setFormData(prev => ({
        ...prev,
        code: code
      }));
    }
  }, [code]);

  const isEmailLocked = Boolean(verifiedEmail);

  useEffect(() => {
    if (verifiedEmail) {
      setFormData(prev => ({
        ...prev,
        email: verifiedEmail
      }));
      setErrors(prev => ({ ...prev, email: undefined }));
    }
  }, [verifiedEmail]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === 'email' && isEmailLocked) {
      return;
    }
    
    if (name === 'code' && isCodeFromUrl) {
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  // Handle input blur for validation
  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newErrors: Partial<FormErrors> = {};
    
    if (name === 'loginId') {
      if (!value.trim()) {
        newErrors.loginId = t('auth.signup.validation.idRequired');
      } else if (value.length < 6) {
        newErrors.loginId = t('auth.signup.validation.idMinLength');
      } else if (!/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,}$/.test(value)) {
        newErrors.loginId = t('auth.signup.validation.idFormat');
      }
    } else if (name === 'email' && !isEmailLocked) {
      if (!value.trim()) {
        newErrors.email = t('auth.signup.validation.emailRequired');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        newErrors.email = t('auth.signup.validation.emailInvalid');
      }
    } else if (name === 'loginPwd') {
      if (!value) {
        newErrors.loginPwd = t('auth.signup.validation.passwordRequired');
      } else if (value.length < 6) {
        newErrors.loginPwd = t('auth.signup.validation.passwordMinLength');
      } else if (!/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,}$/.test(value)) {
        newErrors.loginPwd = t('auth.signup.validation.passwordFormat');
      }
    } else if (name === 'loginPwd_confirm') {
      if (!value) {
        newErrors.loginPwd_confirm = t('auth.signup.validation.confirmPasswordRequired');
      } else if (formData.loginPwd !== value) {
        newErrors.loginPwd_confirm = t('auth.signup.validation.passwordsNotMatch');
      }
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(prev => ({ ...prev, ...newErrors }));
    }
  };

  const handleCheckDuplication = async () => {
    if (!formData.loginId.trim()) {
      setErrors(prev => ({ ...prev, id: t('auth.signup.validation.idCheckFirst') }));
      return;
    }

    if (formData.loginId.length < 6) {
      setErrors(prev => ({ ...prev, id: t('auth.signup.validation.idMinLength') }));
      return;
    }

    if (!/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,}$/.test(formData.loginId)) {
      setErrors(prev => ({ ...prev, id: t('auth.signup.validation.idFormat') }));
      return;
    }

    try {
      const response = await checkLoginIdMutation.mutateAsync(formData.loginId);

      if (response.status === 0) {
        setErrors(prev => ({ ...prev, loginId: undefined }));
        success(t('auth.signup.validation.idAvailable'), '', 3000);
      } else {
        setErrors(prev => ({ ...prev, loginId: t('auth.signup.validation.idTaken') }));
      }
    } catch (error) {
      setErrors(prev => ({ ...prev, loginId: t('auth.signup.validation.idCheckFailed') }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // ID validation
    if (!formData.loginId.trim()) {
      newErrors.loginId = t('auth.signup.validation.idRequired');
    } else if (formData.loginId.length < 6) {
      newErrors.loginId = t('auth.signup.validation.idMinLength');
    } else if (!/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,}$/.test(formData.loginId)) {
      newErrors.loginId = t('auth.signup.validation.idFormat');
    }

    // Email validation (skip when email is locked/verified)
    if (!isEmailLocked) {
      if (!formData.email.trim()) {
        newErrors.email = t('auth.signup.validation.emailRequired');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        newErrors.email = t('auth.signup.validation.emailInvalid');
      }
    }

    // Password validation
    if (!formData.loginPwd) {
      newErrors.loginPwd = t('auth.signup.validation.passwordRequired');
    } else if (formData.loginPwd.length < 6) {
      newErrors.loginPwd = t('auth.signup.validation.passwordMinLength');
    } else if (!/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,}$/.test(formData.loginPwd)) {
      newErrors.loginPwd = t('auth.signup.validation.passwordFormat');
    }

    // Confirm Password validation
    if (!formData.loginPwd_confirm) {
      newErrors.loginPwd_confirm = t('auth.signup.validation.confirmPasswordRequired');
    } else if (formData.loginPwd !== formData.loginPwd_confirm) {
      newErrors.loginPwd_confirm = t('auth.signup.validation.passwordsNotMatch');
    }

    // Age confirmation validation
    if (!ageConfirmed) {
      newErrors.ageConfirmation = t('auth.signup.validation.ageConfirmationRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Step 1: No verified email yet → send verification code and hand off to flow
    if (onSendVerificationCode && !verifiedEmail) {
      try {
        setIsSendingCode(true);
        setErrors((prev) => ({ ...prev, general: undefined }));
        const emailCheckResult = await checkEmail(formData.email);
        if (emailCheckResult.status === 1) {
          setErrors((prev) => ({ ...prev, email: t('auth.signup.validation.emailAlreadyRegistered') }));
          return;
        }
        info(t('auth.signup.messages.sendingCode', 'Sending code…'), t('auth.signup.messages.sendingCodeMessage', 'Sending verification code to your email.'));
        await emailVerificationService.sendVerificationEmail({ email: formData.email });
        success(t('auth.signup.messages.codeSent', 'Code sent'), t('auth.signup.messages.codeSentMessage', 'Enter the verification code in the next step.'));
        onSendVerificationCode({
          loginId: formData.loginId,
          email: formData.email,
          loginPwd: formData.loginPwd,
          loginPwd_confirm: formData.loginPwd_confirm,
          code: formData.code,
        });
      } catch (err) {
        setErrors({
          general: err instanceof Error ? err.message : t('auth.signup.validation.signupFailed'),
        });
      } finally {
        setIsSendingCode(false);
      }
      return;
    }

    try {
      info(t('auth.signup.messages.creatingAccount'), t('auth.signup.messages.creatingAccountMessage'));

      const signupData: Record<string, any> = {
        loginId: formData.loginId,
        loginPwd: formData.loginPwd,
        loginPwd_confirm: formData.loginPwd_confirm,
        code: formData.code,
      };

      if (!isEmailLocked) {
        signupData.email = formData.email;
      } else {
        signupData.email = verifiedEmail;
      }

      const response = await signUpMutation.mutateAsync(signupData);
      
      if (response.status === 1) {
        const fieldErrors: FormErrors = {};
        
        // Check if response has errors object with field-specific errors
        if (response.errors && typeof response.errors === 'object') {
          // Map backend error fields to form error fields and translate them
          if (response.errors.loginId && typeof response.errors.loginId === 'string') {
            fieldErrors.loginId = translateBackendError(response.errors.loginId);
          }
          if (response.errors.loginPwd && typeof response.errors.loginPwd === 'string') {
            fieldErrors.loginPwd = translateBackendError(response.errors.loginPwd);
          }
          if (response.errors.loginPwd_confirm && typeof response.errors.loginPwd_confirm === 'string') {
            fieldErrors.loginPwd_confirm = translateBackendError(response.errors.loginPwd_confirm);
          }
          if (response.errors.email && typeof response.errors.email === 'string') {
            fieldErrors.email = translateBackendError(response.errors.email);
          }
          if (response.errors.code && typeof response.errors.code === 'string') {
            fieldErrors.code = translateBackendError(response.errors.code);
          }
        }
        
        // If no field-specific errors found, check message.warning as fallback
        if (Object.keys(fieldErrors).length === 0 && response.message?.warning) {
          const errorMessage = response.message.warning;
          const errorLower = errorMessage.toLowerCase();
          
            // Try to infer field from error message and translate
            if (errorLower.includes('loginid') || errorLower.includes('login id') || errorLower.includes('id')) {
              fieldErrors.loginId = translateBackendError(errorMessage);
            } else if (errorLower.includes('email')) {
              fieldErrors.email = translateBackendError(errorMessage);
            } else if (errorLower.includes('password') || errorLower.includes('pwd')) {
                fieldErrors.loginPwd = translateBackendError(errorMessage);
            } else {
              // Unknown error, translate if possible
              fieldErrors.general = translateBackendError(errorMessage);
          }
        }
        
        // If still no errors, set a general error
        if (Object.keys(fieldErrors).length === 0) {
          fieldErrors.general = t('auth.signup.validation.signupFailed');
        }
        
        setErrors(fieldErrors);
        return;
      }

      success(t('auth.signup.messages.signupComplete'), t('auth.signup.messages.signupCompleteMessage'));
      
      // Link loginId to verified email for password reset functionality
      const emailToLink = verifiedEmail || formData.email;
      if (emailToLink && formData.loginId) {
        try {
          await linkLoginIdToEmail(emailToLink, formData.loginId);
        } catch (linkError) {
          console.warn('Failed to link loginId to email:', linkError);
          // Don't block signup success if linking fails
        }
      }
      
      // Call success callback if provided
      if (onSignupSuccess) {
        onSignupSuccess({
          loginId: formData.loginId,
          name: formData.loginId, // Use loginId as name since name field is removed
          email: emailToLink
        });
      }
      googleAnalytics.trackSignup('email');
      
      // Close modal and reset form
      onClose();
      resetForm();
      
    } catch (error: any) {
      console.error('Signup failed:', error);
      
      const fieldErrors: FormErrors = {};
      
      // Check if error has response data with field-specific errors
      const errorResponse = error?.response;
      
      if (errorResponse?.errors && typeof errorResponse.errors === 'object') {
        // Map backend error fields to form error fields
        if (errorResponse.errors.loginId) {
          fieldErrors.loginId = translateBackendError(errorResponse.errors.loginId);
        }
        if (errorResponse.errors.loginPwd) {
          fieldErrors.loginPwd = translateBackendError(errorResponse.errors.loginPwd);
        }
        if (errorResponse.errors.loginPwd_confirm) {
          fieldErrors.loginPwd_confirm = translateBackendError(errorResponse.errors.loginPwd_confirm);
        }
        if (errorResponse.errors.email) {
          fieldErrors.email = translateBackendError(errorResponse.errors.email);
        }
        if (errorResponse.errors.code) {
          fieldErrors.code = translateBackendError(errorResponse.errors.code);
        }
      }
      
      // If no field-specific errors found, check message.warning
      if (Object.keys(fieldErrors).length === 0) {
        const warningMessage = errorResponse?.message?.warning || 
                              (error instanceof Error ? error.message : null);
        
        if (warningMessage) {
          const errorLower = warningMessage.toLowerCase();
          
          if (errorLower.includes('login id') || errorLower.includes('loginid') || errorLower.includes('id already')) {
            fieldErrors.loginId = translateBackendError(warningMessage);
          } else if (errorLower.includes('email')) {
            fieldErrors.email = translateBackendError(warningMessage);
          } else if (errorLower.includes('password') || errorLower.includes('pwd')) {
            fieldErrors.loginPwd = translateBackendError(warningMessage);
      } else {
            fieldErrors.general = translateBackendError(warningMessage);
          }
        } else {
          fieldErrors.general = t('auth.signup.validation.signupFailed');
      }
      }
      
      setErrors(fieldErrors);
    }
  };

  const resetForm = () => {
    setFormData({
      loginId: '',
      email: '',
      loginPwd: '',
      loginPwd_confirm: '',
      code: code,
    });
    setAgeConfirmed(false);
    setErrors({});
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-dark-800 rounded-lg w-full max-w-2xl relative my-auto max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-dark-700">
          <div className="text-center flex-1">
            <Logo />
            <div className="text-white text-xl mt-1">{t('auth.signup.title')}</div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-300 transition-colors duration-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* General Error Display */}
          {errors.general && (
            <div className="bg-red-900/20 border border-red-500 rounded-lg p-4 mb-4">
              <p className="text-red-400 text-sm">{errors.general}</p>
            </div>
          )}
          
          {/* ID Section */}
          <div>
            <label className="block text-white text-sm font-medium mb-2">{t('auth.signup.id')}</label>
            <div className="relative">
              <input
                type="text"
                name="loginId"
                value={formData.loginId}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                placeholder={t('auth.signup.placeholders.id')}
                className={`w-full bg-dark-700 border rounded-lg px-4 pr-32 py-3 text-white placeholder-gray-400 focus:outline-none transition-colors duration-200 ${
                  errors.loginId ? 'border-red-500 focus:border-red-400' : 'border-dark-600 focus:border-green-400'
                }`}
              />
              <button
                type="button"
                onClick={handleCheckDuplication}
                disabled={checkLoginIdMutation.isPending}
                className={`absolute inset-y-2 right-2 px-3 text-xs sm:text-sm rounded-lg transition-colors duration-200 ${
                  checkLoginIdMutation.isPending 
                    ? 'bg-gray-500 text-gray-200 cursor-not-allowed' 
                    : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
              >
                {checkLoginIdMutation.isPending ? t('auth.signup.checking') : t('auth.signup.check')}
              </button>
            </div>
            {errors.loginId && (
              <p className="text-red-400 text-xs mt-1">{errors.loginId}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-white text-sm font-medium mb-2">{t('auth.signup.email')}</label>
            <input
              type="email"
              name="email"
              value={verifiedEmail || formData.email}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              placeholder={t('auth.signup.placeholders.email')}
              readOnly={isEmailLocked}
              disabled={isEmailLocked}
              className={`w-full border rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none transition-colors duration-200 ${
                errors.email ? 'border-red-500 focus:border-red-400' : 'border-dark-600 focus:border-green-400'
              } ${isEmailLocked ? 'bg-dark-700/70 text-gray-300 cursor-not-allowed' : 'bg-dark-700'}`}
            />
            {errors.email && (
              <p className="text-red-400 text-xs mt-1">{errors.email}</p>
            )}
            {isEmailLocked && (
              <p className="text-green-400 text-xs mt-1">{t('auth.signup.messages.emailVerified')}</p>
            )}
          </div>

          {/* Password and Confirm Password Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-white text-sm font-medium mb-2">{t('auth.signup.password')}</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="loginPwd"
                  value={formData.loginPwd}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  placeholder={t('auth.signup.placeholders.password')}
                  className={`w-full bg-dark-700 border rounded-lg px-4 py-3 pr-10 text-white placeholder-gray-400 focus:outline-none transition-colors duration-200 ${
                    errors.loginPwd ? 'border-red-500 focus:border-red-400' : 'border-dark-600 focus:border-green-400'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-300 hover:text-white transition-colors duration-200"
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.loginPwd && (
                <p className="text-red-400 text-xs mt-1">{errors.loginPwd}</p>
              )}
            </div>
            <div>
              <label className="block text-white text-sm font-medium mb-2">{t('auth.signup.confirmPassword')}</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="loginPwd_confirm"
                  value={formData.loginPwd_confirm}
                  onChange={handleInputChange}
                  onBlur={handleInputBlur}
                  placeholder={t('auth.signup.placeholders.confirmPassword')}
                  className={`w-full bg-dark-700 border rounded-lg px-4 py-3 pr-10 text-white placeholder-gray-400 focus:outline-none transition-colors duration-200 ${
                    errors.loginPwd_confirm ? 'border-red-500 focus:border-red-400' : 'border-dark-600 focus:border-green-400'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-300 hover:text-white transition-colors duration-200"
                >
                  {showConfirmPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.loginPwd_confirm && (
                <p className="text-red-400 text-xs mt-1">{errors.loginPwd_confirm}</p>
              )}
            </div>
          </div>

          {/* Referral Code */}
            <div>
              <label className="block text-white text-sm font-medium mb-2">{t('auth.signup.referralCode')}</label>
              <input
                type="text"
                name="code"
                value={formData.code}
                onChange={handleInputChange}
                disabled={isCodeFromUrl}
                placeholder={t('auth.signup.placeholders.referralCode')}
                className={`w-full border rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none transition-colors duration-200 ${
                  isCodeFromUrl 
                    ? 'bg-dark-700/70 text-gray-300 cursor-not-allowed' 
                    : 'bg-dark-700'
                } ${
                  errors.code ? 'border-red-500 focus:border-red-400' : 'border-dark-600 focus:border-green-400'
                }`}
              />
              {isCodeFromUrl && (
                <p className="text-green-400 text-xs mt-1">{t('auth.signup.messages.referralCodeAutoFilled')}</p>
              )}
              {errors.code && (
                <p className="text-red-400 text-xs mt-1">{errors.code}</p>
              )}
          </div>

          {/* Age Confirmation Checkbox (18+) */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={ageConfirmed}
                onChange={(e) => {
                  setAgeConfirmed(e.target.checked);
                  if (e.target.checked && errors.ageConfirmation) {
                    setErrors(prev => ({ ...prev, ageConfirmation: undefined }));
                  }
                }}
                className="h-5 w-5 rounded border-2 border-dark-500 bg-dark-700 text-primary-500 focus:ring-2 focus:ring-primary-500/50 focus:ring-offset-0 cursor-pointer"
              />
              <span className={`text-sm font-medium select-none ${
                ageConfirmed ? 'text-green-400' : errors.ageConfirmation ? 'text-red-400' : 'text-gray-300 group-hover:text-white'
              }`}>
                {t('auth.signup.ageConfirmation')}
              </span>
            </label>
            {errors.ageConfirmation && (
              <p className="text-red-400 text-xs mt-1">{errors.ageConfirmation}</p>
            )}
          </div>

          {/* Sign up / Send code Button */}
          <button
            type="submit"
            disabled={signUpMutation.isPending || isSendingCode || isGoogleLoggingIn || isMicrosoftLoggingIn}
            className={`w-full text-white font-medium py-3 rounded-lg transition-all duration-200 ${
              signUpMutation.isPending || isSendingCode || isGoogleLoggingIn || isMicrosoftLoggingIn
                ? 'bg-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800'
            }`}
          >
            {signUpMutation.isPending
              ? t('common.loading')
              : isSendingCode
                ? t('auth.signup.messages.sendingCode', 'Sending code…')
                : onSendVerificationCode && !verifiedEmail
                  ? t('auth.signup.messages.sendVerificationCode', 'Send verification code')
                  : t('common.signup')}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-dark-600"></div>
            <span className="text-gray-400 text-sm">{t('auth.login.orContinueWith', 'or continue with')}</span>
            <div className="flex-1 h-px bg-dark-600"></div>
          </div>

          {/* Social Login Buttons */}
          <div className="flex flex-col items-center gap-3">
            {/* Google Sign In */}
            <GoogleLoginButton
              onSuccess={(accessToken) => {
                handleGoogleLogin(accessToken, formData.code);
              }}
              onError={(error) => {
                setAuthErrors({ general: error.message || t('auth.google.error', 'Google sign-up failed') });
              }}
            />
            
            {/* Microsoft Sign In */}
            <MicrosoftLoginButton
              onSuccess={(accessToken) => {
                handleMicrosoftLogin(accessToken, formData.code);
              }}
              onError={(error) => {
                setAuthErrors({ general: error.message || 'Microsoft sign-up failed' });
              }}
            />
          </div>

          {/* Google Auth Error */}
          {authErrors.general && (
            <div className="bg-red-900/30 border border-red-500 text-red-200 text-sm rounded-lg px-4 py-2">
              {authErrors.general}
            </div>
          )}

          {/* Footer */}
          <div className="text-center">
            <span className="text-gray-300 text-sm">{t('auth.signup.haveAccount')} </span>
            <button
              type="button"
              onClick={onSwitchToLogin}
              className="text-yellow-400 hover:text-yellow-300 text-sm font-medium transition-colors duration-200"
            >
              {t('auth.signup.loginHere')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignupModal; 